import React from 'react';
import { TileLayer, GeoJSON, MapContainer, Circle, Pane, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { TAILLE_TEXTE } from './echartsTheme';
import { couleurMonochromeContinue, couleurDivergenteContinue } from '../constants';

// Échelle rose clair -> bordeaux (maquette Impacts globaux), palette par
// défaut. D'autres pages (Comparer) utilisent la même carte avec une palette
// différente (bleus pour la phénologie, teinte de l'aléa sélectionné pour la
// fréquence des aléas, dégradé rouge/bleu pour l'eau) — voir `palette`.
const PALETTE_ROSE = [
  { seuil: 30, couleur: '#7B1F2B' },
  { seuil: 25, couleur: '#A03A45' },
  { seuil: 20, couleur: '#BE6069' },
  { seuil: 15, couleur: '#D68C93' },
  { seuil: 10, couleur: '#E9B4B9' },
  { seuil: 0, couleur: '#F7DCDF' },
];

/**
 * Sous-ensemble de `palette` réellement couvert par l'intervalle [min, max] :
 * si toutes les valeurs sont positives (ou toutes négatives), les paliers de
 * l'autre côté du zéro ne doivent pas apparaître dans la légende.
 */
function paliersVisibles(palette, min, max) {
  const indexPour = (valeur) => {
    const idx = palette.findIndex((p) => valeur > p.seuil);
    return idx === -1 ? palette.length - 1 : idx;
  };
  const debut = Math.min(indexPour(max), indexPour(min));
  const fin = Math.max(indexPour(max), indexPour(min));
  return palette.slice(debut, fin + 1);
}

/**
 * Recadre la carte à chaque fois que l'emprise change (ex. changement
 * d'échelle géographique PRA -> Région) : `whenReady` sur MapContainer ne se
 * déclenche qu'une fois à la création de la carte, jamais quand `data`
 * change ensuite, donc changer d'échelle sans ça laissait la carte zoomée
 * sur l'emprise de la toute première échelle affichée.
 */
function AjusterEmprise({ extent }) {
  const map = useMap();
  React.useEffect(() => {
    map.flyToBounds([
      [extent[1], extent[0]],
      [extent[3], extent[2]],
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extent[0], extent[1], extent[2], extent[3]]);
  return null;
}

/**
 * Carte du risque moyen de la PRA de l'exploitation et de ses voisines.
 *
 * Même contrat que les cartes de risques actuels : un FeatureCollection dont
 * chaque feature porte `idPetiteRegionAgricole`, `libelle` et la valeur de
 * risque. La PRA de l'exploitation est soulignée par un contour épais.
 *
 * @param data     FeatureCollection
 * @param champ    propriété à colorer (par défaut risqueMoyen)
 * @param idPraExploitation  PRA de l'utilisateur, mise en évidence
 * @param palette  dégradé DISCRET de couleurs (usage historique, Impacts
 *                 globaux/Risque actuel : seuils fixes indépendants des
 *                 données affichées) : [{ seuil, couleur }], trié par seuil
 *                 décroissant. Ignoré si `continu` est fourni.
 * @param continu  dégradé CONTINU calculé sur les données réellement
 *                 affichées (Comparer / Phénologie, Fréquence aléas, Eau) :
 *                 { type: 'mono', base, min, max } (min quasi blanc, base =
 *                 couleur au milieu de [min,max], max très foncé) ou
 *                 { type: 'diverge', positif, negatif, min, max } (blanc
 *                 uniquement à 0 ; si min/max ne traversent pas 0, l'autre
 *                 couleur n'apparaît jamais). `min`/`max` doivent être les
 *                 bornes GLOBALES des 2-3 cartes affichées ensemble (pas les
 *                 bornes de cette seule carte), sinon chaque carte aurait sa
 *                 propre échelle et ne serait plus comparable aux autres.
 * @param unite    unité affichée dans la légende et les popups (par défaut %)
 * @param pointExploitation  { lat, lng } : si fourni, un point noir marque
 *                 le siège social de l'exploitation (Comparer / Risque
 *                 actuel) et les contours restent uniformes — pas de contour
 *                 épais sur la zone de l'exploitation dans ce cas (l'un ou
 *                 l'autre, pas les deux).
 * @param afficherLegende  false quand plusieurs cartes côte à côte partagent
 *                 une légende unique affichée par la page appelante (Comparer
 *                 / Phénologie, Fréquence aléas, Eau) — true par défaut pour
 *                 les usages à une seule carte (Impacts globaux, Risque
 *                 actuel).
 */
export default function CarteRisquePRA({
  data, champ = 'risqueMoyen', idPraExploitation, palette = PALETTE_ROSE, continu, unite = '%',
  pointExploitation, afficherLegende = true,
}) {
  if (!data?.features?.length) {
    return <p className="tdb-chargement">Carte indisponible.</p>;
  }

  const bbox = require('geojson-bbox');
  const extent = bbox(data);

  const valeurs = data.features.map((f) => Number(f.properties[champ]));
  const min = continu ? continu.min : Math.min(...valeurs);
  const max = continu ? continu.max : Math.max(...valeurs);

  function couleurPour(valeur) {
    if (continu) {
      return continu.type === 'diverge'
        ? couleurDivergenteContinue(continu.positif, continu.negatif, continu.min, continu.max, valeur)
        : couleurMonochromeContinue(continu.base, continu.min, continu.max, valeur);
    }
    const palier = palette.find((p) => valeur > p.seuil);
    return palier ? palier.couleur : '#ffffff';
  }

  function style(feature) {
    const estExploitation =
      !pointExploitation &&
      feature.properties.idPetiteRegionAgricole === idPraExploitation;
    return {
      fillColor: couleurPour(Number(feature.properties[champ])),
      weight: estExploitation ? 3 : 0.5,
      opacity: 1,
      color: estExploitation ? '#000000' : '#666666',
      dashArray: estExploitation ? undefined : '3',
      fillOpacity: 0.8,
    };
  }

  function onEachFeature(feature, layer) {
    const { libelle, idPetiteRegionAgricole } = feature.properties;
    const valeur = feature.properties[champ];
    // Le "(votre exploitation)" ne veut dire quelque chose que quand on
    // souligne une PRA par idPraExploitation ; avec un point (pointExploitation),
    // idPraExploitation est undefined et undefined === undefined vaudrait
    // "vrai" sur CHAQUE entité (RA/Département/Région n'ont pas ce champ) —
    // d'où la mention affichée partout.
    const marque =
      !pointExploitation && idPraExploitation && idPetiteRegionAgricole === idPraExploitation
        ? ' <i>(votre exploitation)</i>'
        : '';
    layer.bindPopup(`<p><b>${libelle}</b>${marque} : ${valeur} ${unite}</p>`);
  }

  return (
    <div className="carte-pra">
      <MapContainer
        zoom={9}
        className="carte-pra-leaflet"
        scrollWheelZoom={false}
        maxZoom={14}
        center={[48.7, 2.7]}
        whenReady={(e) => {
          e.target.flyToBounds([
            [extent[1], extent[0]],
            [extent[3], extent[2]],
          ]);
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <AjusterEmprise extent={extent} />
        {/* key force le redessin quand l'horizon change : GeoJSON ne
            réapplique pas `style` tout seul sur un changement de props. */}
        <GeoJSON key={champ + min + max} data={data} style={style} onEachFeature={onEachFeature} />

        {pointExploitation && (
          // Pane dédiée avec un z-index au-dessus de l'overlayPane (400) :
          // sans ça, le point se retrouvait sous les polygones dès que
          // GeoJSON se redessinait après lui (changement de niveau de
          // risque), l'ordre d'ajout au DOM changeant à ce moment-là.
          <Pane name="point-exploitation" style={{ zIndex: 650 }}>
            <Circle
              center={[pointExploitation.lat, pointExploitation.lng]}
              radius={1500}
              pathOptions={{ color: '#000000', fillColor: '#000000', fillOpacity: 1 }}
            />
          </Pane>
        )}
      </MapContainer>

      {afficherLegende && (
        <div className="carte-pra-legende">
          <span style={{ fontSize: TAILLE_TEXTE }}>{min} {unite}</span>
          <div className="carte-pra-degrade">
            {continu ? (
              <span
                style={{
                  flex: 1,
                  background: `linear-gradient(to right, ${couleurPour(min)}, ${couleurPour(max)})`,
                }}
              />
            ) : (
              [...paliersVisibles(palette, min, max)].reverse().map((p) => (
                <span key={p.seuil} style={{ backgroundColor: p.couleur }} />
              ))
            )}
          </div>
          <span style={{ fontSize: TAILLE_TEXTE }}>{max} {unite}</span>
        </div>
      )}
    </div>
  );
}
